import { YearPickerInput } from '@mantine/dates'
import moment from 'moment'
import type React from 'react'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import Calendar03Icon from '../../../../assets/icons/calendar-03-stroke-rounded (1)'
import PageWrapper from '../../../../components/Pagewrapper'
import {
  useGetOkxAccountsQuery,
  useGetOkxPortfolioMetricsQuery,
} from '../../../../redux/services/coreApi'
import type { RootState } from '../../../../redux/store'
import { colorByPerformance } from '../../../../utils/colorByPerformance'
import { formatNumber } from '../../../../utils/formatters'
import LargeNumber from './components/LargeNumber'
import LineChart from './components/LineChart'

type ViewGranularity = 'daily' | 'monthly'

const OkxInvestorScreen: React.FC = () => {
  const [selectedCurrency, setSelectedCurrency] = useState<string>('EUR')
  const [year, setYear] = useState<Date | null>(new Date())
  const [viewGranularity, setViewGranularity] =
    useState<ViewGranularity>('monthly')
  const [selectedAccountId, setSelectedAccountId] = useState<string>('all')

  const sessionUser = useSelector((state: RootState) => state.session.user)

  // Get OKX accounts for this investor
  const { data: okxAccounts, isLoading: isLoadingAccounts } =
    useGetOkxAccountsQuery({
      investorId: sessionUser.id,
    })

  // Get portfolio metrics (invested amount, performance, balance, chart data) from the backend
  const { data: portfolioMetrics, isLoading: isLoadingMetrics } =
    useGetOkxPortfolioMetricsQuery({
      investorId: sessionUser.id,
      okxAccountId: selectedAccountId === 'all' ? undefined : selectedAccountId,
      startDate: moment([
        year?.getFullYear() ?? new Date().getFullYear(),
        0,
        1,
      ]).format('YYYY-MM-DD'),
      endDate: moment([
        year?.getFullYear() ?? new Date().getFullYear(),
        11,
        31,
      ]).format('YYYY-MM-DD'),
      granularity: viewGranularity,
      currency: selectedCurrency,
    })

  const accountValue = portfolioMetrics?.currentBalance ?? 0
  const investedAmount = portfolioMetrics?.investedAmount ?? null

  const overallPerformance =
    portfolioMetrics?.overallPerformancePercent != null
      ? portfolioMetrics.overallPerformancePercent * 100
      : 0

  const moneyDiff = portfolioMetrics?.tradingPnl ?? 0

  // Chart data from backend-computed period performance (adjusted for deposits/withdrawals)
  const chartData = (() => {
    if (!portfolioMetrics?.periodPerformance?.length) return []

    return portfolioMetrics.periodPerformance.map(item => ({
      month: moment(item.date),
      data: {
        endOfMonthCapital: item.endOfPeriodBalance,
        performancePercent: item.performancePercent,
        performancePercentToDate: item.performancePercentToDate,
        deposited: item.deposited,
        withdrawn: item.withdrawn,
        performanceFee: 0,
        date: item.date,
        startOfMonthCapital: 0,
        startOfMonthHwm: 0,
        endOfMonthHwm: 0,
        performance: 0,
        performanceAboveHwm: 0,
      },
    }))
  })()

  const hasNoData =
    !isLoadingMetrics &&
    !portfolioMetrics?.currentBalance &&
    (!portfolioMetrics?.periodPerformance ||
      portfolioMetrics.periodPerformance.length === 0)

  const hasNoAccounts =
    !isLoadingAccounts && (!okxAccounts || okxAccounts.length === 0)

  return (
    <PageWrapper>
      <div className="grid grid-cols-12 gap-5">
        <div className="col-span-full lg:col-span-9">
          <div className="flex items-center gap-4 mb-4 flex-wrap">
            <YearPickerInput
              leftSection={
                <Calendar03Icon className="w-4 h-4 -mr-1 text-gray-500" />
              }
              leftSectionPointerEvents="none"
              placeholder="Select year"
              value={year}
              onChange={setYear}
              className="w-[100px] font-regular"
              radius={'md'}
              size="sm"
            />

            {/* Account Selector */}
            {okxAccounts && okxAccounts.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Account:
                </span>
                <select
                  value={selectedAccountId}
                  onChange={e => setSelectedAccountId(e.target.value)}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="all">All Accounts Combined</option>
                  {okxAccounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.name} {!account.isActive && '(Inactive)'}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* View Granularity Toggle */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Chart view:
              </span>
              <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setViewGranularity('monthly')}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${
                    viewGranularity === 'monthly'
                      ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setViewGranularity('daily')}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${
                    viewGranularity === 'daily'
                      ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Daily
                </button>
              </div>
            </div>

            {/* Currency Toggle */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Currency:
              </span>
              <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                {['EUR', 'USD'].map(ccy => (
                  <button
                    key={ccy}
                    onClick={() => setSelectedCurrency(ccy)}
                    className={`px-3 py-1 text-sm rounded-md transition-colors ${
                      selectedCurrency === ccy
                        ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    {ccy}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {hasNoAccounts ? (
            // No accounts state
            <div className="bg-white dark:bg-brand-dark-card border border-solid border-black/10 shadow-sm rounded-xl p-8 text-center">
              <div className="max-w-md mx-auto">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  No Accounts Connected
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  You don't have any trading accounts set up yet. Contact your
                  administrator for assistance.
                </p>
                <div className="space-y-2">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Once your accounts are configured, you'll be able to view
                    your portfolio balance and performance here.
                  </p>
                </div>
              </div>
            </div>
          ) : hasNoData ? (
            // Empty state
            <div className="bg-white dark:bg-brand-dark-card border border-solid border-black/10 shadow-sm rounded-xl p-8 text-center">
              <div className="max-w-md mx-auto">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  No Balance Data Available
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  There's no balance history data for {selectedCurrency} in{' '}
                  {year?.getFullYear()}
                  {selectedAccountId !== 'all' && okxAccounts && (
                    <span>
                      {' '}
                      for account "
                      {
                        okxAccounts.find(acc => acc.id === selectedAccountId)
                          ?.name
                      }
                      "
                    </span>
                  )}
                  . This could be because:
                </p>
                <ul className="text-sm text-gray-500 dark:text-gray-400 text-left space-y-1 mb-6">
                  <li>
                    • No balance data exists for the selected year
                    {selectedAccountId !== 'all' ? ' and account' : ''}
                  </li>
                  <li>• Data collection hasn't started yet</li>
                  <li>• The selected account might be inactive</li>
                </ul>
                <div className="space-y-2">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Try selecting a different year
                    {okxAccounts && okxAccounts.length > 1 ? ', account,' : ''}{' '}
                    or currency, or contact support if you believe this is an
                    error.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            // Normal content
            <>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 mb-2">
                <LargeNumber
                  title="Invested amount"
                  value={
                    investedAmount !== null
                      ? formatNumber(
                          investedAmount,
                          {
                            currencyType: 'FiatCurrency',
                            isoCode: selectedCurrency,
                          },
                          2
                        )
                      : 'N/A'
                  }
                  color="text-gray-800"
                  isLoading={isLoadingMetrics}
                />
                <LargeNumber
                  title="Account value"
                  value={`${formatNumber(
                    accountValue,
                    { currencyType: 'FiatCurrency', isoCode: selectedCurrency },
                    2
                  )}`}
                  color="text-gray-800"
                  isLoading={isLoadingMetrics}
                />
                <LargeNumber
                  title={`Overall ${selectedAccountId === 'all' ? 'Combined ' : ''}Performance`}
                  value={`${overallPerformance.toFixed(2)}%`}
                  color={colorByPerformance(overallPerformance)}
                  isLoading={isLoadingMetrics}
                  renderRight={
                    <p
                      className={`text-sm font-medium ${colorByPerformance(
                        moneyDiff
                      )}`}
                    >{`${moneyDiff > 0 ? '+' : ''}${formatNumber(
                      moneyDiff,
                      {
                        currencyType: 'FiatCurrency',
                        isoCode: selectedCurrency,
                      },
                      2
                    )}`}</p>
                  }
                />
              </div>
              <div className="grid grid-cols-1">
                <LineChart
                  data={chartData}
                  currencyIsoCode={selectedCurrency}
                  viewGranularity={viewGranularity}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </PageWrapper>
  )
}

export default OkxInvestorScreen
